import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Star, ShieldCheck, MapPin, ChevronRight, Send, Paperclip, X, ArrowLeft } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { adminNav } from "../../components/shared/navConfig";
import api from "../../lib/api";

const STEPS = [
  { key: "student",  label: "Pick student" },
  { key: "details",  label: "Task details" },
  { key: "extras",   label: "Attachments · deadline · budget" },
  { key: "review",   label: "Review & send" },
];

export default function AdminDirectWorkNew() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ city: "", skill: "", min_rating: 0, verified_only: true, available_only: false });
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    title: "", work_type: "", description: "", instructions: "",
    required_skills: "", location: "", city: "",
    start_at: "", deadline: "", budget: "", priority: "normal", notes: "",
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadStudents = async () => {
    try {
      const { data } = await api.get("/admin/students/directory", {
        params: { q: q || undefined, city: filters.city || undefined, skill: filters.skill || undefined,
                  min_rating: filters.min_rating || 0, verified_only: filters.verified_only, available_only: filters.available_only, limit: 60 },
      });
      setStudents(data);
    } catch (_) { toast.error("Failed to load students"); }
  };
  useEffect(() => { loadStudents(); /* eslint-disable-next-line */ }, []);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/uploads?purpose=direct_work", fd, { headers: { "Content-Type": "multipart/form-data" }});
        setPendingFiles(p => [...p, { file_id: data.file_id, original_filename: data.original_filename }]);
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    }
    e.target.value = "";
  };

  const send = async (asDraft = false) => {
    if (!selected) return toast.error("Pick a student first");
    if (!form.title || !form.description) return toast.error("Title and description are required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/direct-work", {
        student_id: selected.user_id,
        title: form.title, work_type: form.work_type, description: form.description,
        instructions: form.instructions,
        required_skills: form.required_skills.split(",").map(s => s.trim()).filter(Boolean),
        location: form.location, city: form.city,
        start_at: form.start_at, deadline: form.deadline,
        budget: Number(form.budget) || 0, priority: form.priority, notes: form.notes,
        attachment_file_ids: pendingFiles.map(f => f.file_id),
        save_as_draft: asDraft,
      });
      toast.success(asDraft ? "Draft saved" : "Task sent");
      navigate(`/admin/direct-work/${data.direct_work_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardShell nav={adminNav} title="Start Direct Work" subtitle="Assign a task or proposal directly to a verified student.">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1" data-testid="adw-new-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button
              onClick={() => setStep(i)}
              className="text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors"
              style={{
                background: step === i ? "rgba(59,130,246,0.10)" : "var(--surface)",
                borderColor: step >= i ? "rgba(59,130,246,0.35)" : "var(--border-strong)",
                color: step >= i ? "var(--brand)" : "var(--text-muted)",
              }}
            >{i + 1}. {s.label}</button>
            {i < STEPS.length - 1 && <ChevronRight size={12} style={{ color: "var(--text-dim)" }}/>}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <div>
          <div className="flex flex-col md:flex-row gap-2 mb-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border flex-1"
                 style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
              <Search size={14} style={{ color: "var(--text-dim)" }}/>
              <input data-testid="adw-search-students"
                     value={q} onChange={e => setQ(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && loadStudents()}
                     placeholder="Search students by name or email…"
                     className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text)" }}/>
            </div>
            <button onClick={loadStudents} className="btn-primary px-4 py-2 rounded-md text-xs" data-testid="adw-search-btn">Search</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <FilterInput placeholder="City" value={filters.city} onChange={v => setFilters(f => ({...f, city: v}))} tid="filter-city"/>
            <FilterInput placeholder="Skill" value={filters.skill} onChange={v => setFilters(f => ({...f, skill: v}))} tid="filter-skill"/>
            <FilterInput placeholder="Min rating (0-5)" type="number" value={filters.min_rating} onChange={v => setFilters(f => ({...f, min_rating: v}))} tid="filter-rating"/>
            <div className="flex items-center gap-3 text-xs px-3" style={{ color: "var(--text-muted)" }}>
              <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={filters.verified_only} onChange={e => setFilters(f => ({...f, verified_only: e.target.checked}))}/> KYC verified</label>
              <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={filters.available_only} onChange={e => setFilters(f => ({...f, available_only: e.target.checked}))}/> Available</label>
            </div>
          </div>

          {students.length === 0 ? (
            <div className="text-sm py-10 text-center card-surface" style={{ color: "var(--text-dim)" }}>No students match these filters.</div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {students.map(s => (
                <button
                  key={s.user_id}
                  data-testid={`adw-student-${s.user_id}`}
                  onClick={() => setSelected(s)}
                  className={`card-surface card-hover p-4 text-left ${selected?.user_id === s.user_id ? "ring-2 ring-blue-500" : ""}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {s.picture ? <img src={s.picture} alt="" className="w-9 h-9 rounded-full object-cover"/>
                              : <div className="w-9 h-9 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center text-xs font-mono">{(s.name || "?").slice(0,1).toUpperCase()}</div>}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{s.name || "Unnamed"}</div>
                      <div className="text-[11px] truncate" style={{ color: "var(--text-dim)" }}>{s.email}</div>
                    </div>
                    {s.kyc_status === "approved" && <ShieldCheck size={14} className="text-emerald-500 flex-shrink-0"/>}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    <div className="inline-flex items-center gap-1 truncate"><MapPin size={11}/> {s.city || "—"}</div>
                    <div className="inline-flex items-center gap-1"><Star size={11} className="text-amber-400"/> {s.rating?.toFixed(1) || "—"}</div>
                    <div className="truncate">{s.college || "—"}</div>
                    <div className="truncate">{s.year || s.degree || "—"}</div>
                  </div>
                  {s.skills?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.skills.slice(0, 4).map(sk => (
                        <span key={sk} className="text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>{sk}</span>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button disabled={!selected} onClick={() => setStep(1)}
                    data-testid="adw-step-next"
                    className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="card-surface p-5 space-y-3 max-w-3xl">
          <Row2>
            <Field label="Work / task title"><input data-testid="adw-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className={ic} placeholder="e.g. Vendor GST reconciliation"/></Field>
            <Field label="Work type"><input data-testid="adw-type" value={form.work_type} onChange={e => setForm({...form, work_type: e.target.value})} className={ic} placeholder="Task / Proposal / Assignment"/></Field>
          </Row2>
          <Field label="Detailed description">
            <textarea data-testid="adw-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={ic + " min-h-24 py-2"} placeholder="Explain the work scope…"/>
          </Field>
          <Field label="Instructions">
            <textarea data-testid="adw-instructions" value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})} className={ic + " min-h-20 py-2"} placeholder="Step-by-step guidance for the student"/>
          </Field>
          <Row2>
            <Field label="Required skills (comma separated)"><input data-testid="adw-skills" value={form.required_skills} onChange={e => setForm({...form, required_skills: e.target.value})} className={ic} placeholder="Tally, GST, Excel"/></Field>
            <Field label="Priority">
              <select data-testid="adw-priority" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className={ic}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </Field>
          </Row2>
          <Row2>
            <Field label="Location"><input data-testid="adw-location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className={ic} placeholder="Address or 'Remote'"/></Field>
            <Field label="City"><input data-testid="adw-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className={ic} placeholder="Pune"/></Field>
          </Row2>
          <div className="flex justify-between pt-3">
            <button onClick={() => setStep(0)} className="btn-ghost px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><ArrowLeft size={12}/> Back</button>
            <button onClick={() => setStep(2)} data-testid="adw-step-next" className="btn-primary px-4 py-2 rounded-md text-sm">Next</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card-surface p-5 space-y-3 max-w-3xl">
          <Row2>
            <Field label="Start date/time"><input data-testid="adw-start" type="datetime-local" value={form.start_at} onChange={e => setForm({...form, start_at: e.target.value})} className={ic}/></Field>
            <Field label="Deadline"><input data-testid="adw-deadline" type="datetime-local" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className={ic}/></Field>
          </Row2>
          <Field label="Budget (₹)"><input data-testid="adw-budget" type="number" min="0" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} className={ic} placeholder="0"/></Field>
          <Field label="Additional notes">
            <textarea data-testid="adw-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className={ic + " min-h-16 py-2"} placeholder="Anything else for the student"/>
          </Field>

          <div>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-dim)" }}>Attachments</div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="btn-ghost px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5 cursor-pointer">
                <Paperclip size={12}/> Add file
                <input data-testid="adw-file" type="file" multiple accept=".pdf,image/*" hidden onChange={onFile}/>
              </label>
              {pendingFiles.map(f => (
                <span key={f.file_id} className="text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1"
                      style={{ borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>
                  {f.original_filename}
                  <button onClick={() => setPendingFiles(p => p.filter(x => x.file_id !== f.file_id))}><X size={10}/></button>
                </span>
              ))}
            </div>
            <div className="text-[10px] mt-1" style={{ color: "var(--text-dim)" }}>PDF and images accepted (10 MB max each).</div>
          </div>

          <div className="flex justify-between pt-3">
            <button onClick={() => setStep(1)} className="btn-ghost px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><ArrowLeft size={12}/> Back</button>
            <button onClick={() => setStep(3)} data-testid="adw-step-next" className="btn-primary px-4 py-2 rounded-md text-sm">Next</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card-surface p-5 max-w-3xl">
          <h3 className="font-display text-lg mb-3" style={{ color: "var(--text)" }}>Review & send</h3>
          <dl className="grid grid-cols-2 gap-y-2 text-sm mb-4">
            <Info k="Student" v={selected?.name}/>
            <Info k="Task title" v={form.title}/>
            <Info k="Priority" v={form.priority}/>
            <Info k="Budget" v={form.budget ? `₹${Number(form.budget).toLocaleString("en-IN")}` : "—"}/>
            <Info k="Deadline" v={form.deadline || "—"}/>
            <Info k="Location" v={form.location || form.city || "Remote"}/>
            <Info k="Attachments" v={`${pendingFiles.length}`}/>
          </dl>
          <div className="text-sm whitespace-pre-line mb-4" style={{ color: "var(--text-muted)" }}>{form.description}</div>
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => send(false)} data-testid="adw-send-task-btn" className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2">
              <Send size={13}/> Send task
            </button>
            <button disabled={busy} onClick={() => send(true)} data-testid="adw-save-draft-btn" className="btn-ghost px-4 py-2 rounded-md text-sm">
              Save draft
            </button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

const ic = "w-full bg-transparent outline-none text-sm px-3 py-2 rounded-md border";
const FilterInput = ({ value, onChange, placeholder, type = "text", tid }) => (
  <input data-testid={`adw-${tid}`} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
         className="bg-transparent outline-none text-xs px-3 py-2 rounded-md border w-full"
         style={{ borderColor: "var(--border-strong)", color: "var(--text)", background: "var(--surface)" }}/>
);
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>{label}</span>
    <div className="mt-1.5 rounded-md border" style={{ borderColor: "var(--border-strong)", background: "var(--surface)", color: "var(--text)" }}>
      {children}
    </div>
  </label>
);
const Row2 = ({ children }) => <div className="grid md:grid-cols-2 gap-3">{children}</div>;
const Info = ({ k, v }) => (<><dt className="text-xs" style={{ color: "var(--text-dim)" }}>{k}</dt><dd className="text-xs" style={{ color: "var(--text)" }}>{v || "—"}</dd></>);
